import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronRightIcon, CreditCardIcon, RefreshCwIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { permitDetailQuestions, permitStatusLabels } from '../data/permis';
import { usePermits } from '../contexts/PermitsContext';
import { PermitProgress } from '../components/permis/PermisList';
import { PermitDetailsPanel } from '../components/permis/PermitDetailsPanel';
import { PermitFeeDialog } from '../components/permis/PermitFeeDialog';
import { RequestMessages } from '../components/demandes/RequestMessages';
const answers: Record<string, string> = {
  'Où en est ma demande de permis ?': 'Votre dossier est en analyse au service de l’urbanisme. Une inspectrice valide les plans et les distances.',
  'Quels documents manquent ?': 'Les documents encore attendus sont listés dans la section « Documents exigés ». Vous pouvez les transmettre dans l’onglet Messages.',
  'Puis-je commencer les travaux ?': 'Non. Les travaux ne peuvent débuter qu’une fois le permis émis et affiché sur le chantier.'
};
type Tab = 'details' | 'messages';
export function PermisDetail() {
  const {
    id
  } = useParams();
  const {
    permits,
    payPermitFee
  } = usePermits();
  const permit = permits.find(item => item.id === id) ?? permits[0];
  const [tab, setTab] = useState<Tab>('details');
  const [asked, setAsked] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const pending = permit.messages.filter(message => message.author === 'ville' && message.awaiting).length;
  const tabs: {
    id: Tab;
    label: string;
    badge?: number;
  }[] = [{
    id: 'details',
    label: 'Détails'
  }, {
    id: 'messages',
    label: 'Messages',
    badge: pending || undefined
  }];
  return <main className="bg-white" data-id="element-3138" data-mp="2rs6bq05hotac">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-3139" data-mp="2ro8rfb76dvov">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-3140" data-mp="2mq76ekq6vmar">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3141" data-mp="2o6kz7fi8kxft">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3142" data-mp="2f89c3ngkwhs4" />
          <Link to="/mes-permis" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3143" data-mp="2fa3ogddsb1kl">
            Mes permis
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3144" data-mp="25ho3r8b3ryw0" />
          <span aria-current="page" data-id="element-3145" data-mp="25gd0d9lkkt5f">{permit.reference}</span>
        </nav>

        <header className="mt-4" data-id="element-3146" data-mp="2aa7ii8lj102e">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-3147" data-mp="1whhy28lnc37e">
            <div data-id="element-3148" data-mp="1wfexiumvarof">
              <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-3149" data-mp="20j353dbi1n9o">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-3150" data-mp="1misdu96a26nz" />
                {permit.service}
              </p>
              <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-ville-navy" data-id="element-3151" data-mp="1rh3lqed0i73k">{permit.title}</h1>
              <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-3152" data-mp="17x52ndi1ych9">
                {permit.category} · Demande {permit.reference}
              </p>
            </div>
            <button type="button" aria-label="Actualiser la demande" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3153" data-mp="1hv2q3ys3858t">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-3154" data-mp="20kir0n7opzpr" />
              Actualiser
            </button>
          </div>

          <div className="mt-5" data-id="element-3155" data-mp="29lyro38p6ngs">
            <PermitProgress status={permit.status} data-id="element-3156" data-mp="29n0et62zn0ev" />
          </div>
        </header>

        <div role="tablist" aria-label="Sections de la demande" className="mt-8 flex gap-6 border-b border-slate-200" data-id="element-3157" data-mp="25kwz259ad9ns">
          {tabs.map(item => {
          const active = tab === item.id;
          return <button key={item.id} type="button" role="tab" id={`onglet-permis-${item.id}`} aria-selected={active} aria-controls={`panneau-permis-${item.id}`} onClick={() => setTab(item.id)} className={`relative -mb-px flex items-center gap-2 pb-3 text-[15px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'text-ville-navy' : 'text-slate-500 hover:text-ville-navy'}`} data-id="element-3158" data-mp="7vrl175cewya">
                {item.label}
                {item.badge && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-ville-lime px-1.5 text-[11px] font-bold text-ville-navy" data-id="element-3159" data-mp="15bf6qhyf6mj3">
                    {item.badge}
                  </span>}
                {active && <motion.span layoutId="onglet-permis" transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-ville-navy" aria-hidden="true" data-id="element-3160" data-mp="2pu7wo147e96y" />}
              </button>;
        })}
        </div>

        {tab === 'details' ? <div role="tabpanel" id="panneau-permis-details" aria-labelledby="onglet-permis-details" className="mt-6" data-id="element-3161" data-mp="265qohqb8qhs1">
            {permit.feeStatus === 'a-payer' && <section aria-labelledby="permis-paiement-titre" className="mb-5 rounded-3xl border-2 border-ville-navy bg-ville-sand p-6" data-id="element-3162" data-mp="226htyj8dmfvf">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ville-deep" data-id="element-3163" data-mp="b7gu3ldd0u9">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-3164" data-mp="eiu0lxyhnx1d" />
                  Demande acceptée le {permit.decisionOn}
                </p>
                <h2 id="permis-paiement-titre" className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-3165" data-mp="jakbq6592otu">
                  Payez les frais pour recevoir votre permis
                </h2>
                <p className="mt-1 text-[15px] text-slate-600" data-id="element-3166" data-mp="ai0cp4l1qi8h">
                  {permit.fee} exigibles maintenant que la Ville a accepté votre demande
                  {permit.feeDueOn ? `, à acquitter avant le ${permit.feeDueOn}` : ''}. Le permis est émis dès
                  le paiement.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3167" data-mp="o83qd75v13mb">
                  <button type="button" onClick={() => setPaying(true)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3168" data-mp="t6ngljrhl1kh">
                    <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-3169" data-mp="31f22lhicinc4" />
                    Payer {permit.fee}
                  </button>
                  <p className="text-[13px] text-slate-500" data-id="element-3170" data-mp="3ag9pz4jbq3rf">En ligne ou au comptoir de l’urbanisme.</p>
                </div>
              </section>}

            {permit.feeStatus === 'paye' && permit.status === 'emis' && permit.fee !== 'Gratuit' && <p className="mb-5 flex items-start gap-2 rounded-2xl bg-ville-mist px-5 py-4 text-sm font-semibold text-ville-deep" data-id="element-3171" data-mp="2imr4e0ymzdkc">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-3172" data-mp="2rps67pggyii7" />
                Frais de {permit.fee} payés · permis émis. Le certificat est dans les fichiers joints.
              </p>}

            <PermitDetailsPanel permit={permit} data-id="element-3173" data-mp="1z1mmn615izjy" />

            <p className="mt-4 text-xs text-slate-500" data-id="element-3174" data-mp="1yys0na2hp6of">Statut actuel : {permitStatusLabels[permit.status]}</p>

            <section aria-labelledby="atna-permis-titre" className="mt-6 rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-3175" data-mp="1igmo46713wmw">
              <h2 id="atna-permis-titre" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-3176" data-mp="1nf6au8tg5cgb">
                <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-3177" data-mp="1sfu47155e5dp" />
                Demander à ATNA
              </h2>

              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-3178" data-mp="1xbssypue1pen">
                {permitDetailQuestions.map(question => <li key={question} data-id="element-3179" data-mp="2667c53i1hk31">
                    <button type="button" onClick={() => setAsked(question)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${asked === question ? 'bg-ville-indigo text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-3180" data-mp="2678nza6vqalt">
                      {question}
                    </button>
                  </li>)}
              </ul>

              {asked && <p className="mt-4 rounded-2xl bg-ville-indigo-soft px-5 py-4 text-sm leading-relaxed text-ville-navy" data-id="element-3181" data-mp="3qqcf2732kw78">
                  {answers[asked]}
                </p>}

              <p className="mt-4 text-xs text-slate-500" data-id="element-3182" data-mp="n8axh67sw73v">
                Les mises à jour de ce dossier apparaissent aussi dans{' '}
                <Link to="/notifications" className="font-bold text-ville-indigo hover:underline" data-id="element-3183" data-mp="dhzimomkwdi7">
                  Notifications
                </Link>
                .
              </p>
            </section>
          </div> : <div role="tabpanel" id="panneau-permis-messages" aria-labelledby="onglet-permis-messages" className="mt-6" data-id="element-3184" data-mp="165lmlxzpxk5l">
            <RequestMessages request={permit} data-id="element-3185" data-mp="3941yost67lzh" />
          </div>}
      </div>

      <PermitFeeDialog permit={paying ? permit : null} onClose={() => setPaying(false)} onPaid={method => payPermitFee(permit.id, method)} data-id="element-3186" data-mp="353hy1f9e208l" />
    </main>;
}